/**
 * Die Geräte hinter einer Zahl des Dashboards (Rückmeldungen 8.1).
 *
 * `/geraete?einsatzbereit=nein` zeigt alle, die nicht einsatzbereit sind,
 * `/geraete?status=defekt` alle defekten, und so weiter (`lokal/geraeteliste.ts`).
 * Aus der lokalen Kopie, also auch offline. Ein Tipp öffnet das Gerät.
 */

import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { useBestand } from "../lokal/bestand.js";
import { filterAus, filtern, titelFuer } from "../lokal/geraeteliste.js";
import { woWar } from "../lokal/verschieben.js";
import { Ladeanzeige, Leer, Nummer, StatusPunkt } from "../ui/bausteine.jsx";

/** Mehr liest niemand am Stück; wer mehr braucht, filtert enger. */
const HOECHSTENS = 300;

export function Geraeteliste() {
  const bestand = useBestand();
  const [parameter] = useSearchParams();
  const filter = useMemo(() => filterAus(parameter), [parameter]);
  const treffer = useMemo(
    () => (bestand === undefined ? [] : filtern(bestand, filter)),
    [bestand, filter],
  );

  if (bestand === undefined) return <Ladeanzeige was="Die Liste" />;

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">{titelFuer(filter, bestand)}</h1>
      <p className="text-sm text-neutral-600 dark:text-neutral-300">
        {treffer.length === 1 ? "1 Gerät" : `${String(treffer.length)} Geräte`} — aus dem Stand auf
        diesem Gerät.
      </p>
      {treffer.length === 0 ? (
        <Leer>Keine.</Leer>
      ) : (
        <ul className="flex flex-col gap-2" aria-label="Geräte">
          {treffer.slice(0, HOECHSTENS).map(({ artikel, zustand }) => (
            <li key={artikel.id}>
              <Link
                to={`/artikel/${artikel.id}`}
                className="flex min-h-16 flex-col justify-center gap-1 rounded-xl bg-white p-3 active:bg-neutral-100 dark:bg-neutral-800 dark:active:bg-neutral-700"
              >
                <span className="font-semibold">{artikel.bezeichnung}</span>
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <Nummer>{artikel.inventarnummer}</Nummer>
                  <StatusPunkt status={zustand.status} />
                  <span className="text-sm text-neutral-600 dark:text-neutral-300">
                    {woWar(bestand, zustand)}
                  </span>
                </span>
              </Link>
            </li>
          ))}
          {treffer.length > HOECHSTENS && (
            <li className="text-sm text-neutral-600 dark:text-neutral-300">
              … und {treffer.length - HOECHSTENS} weitere
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
